import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "@phosphor-icons/react";
import { MaskText } from "./motion";
import { scrollToId } from "@/lib/scroll";

const STATS = [
  { k: "2025", v: "Freshly Founded" },
  { k: "10+", v: "Projects Delivered" },
  { k: "100%", v: "Focused On You" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen bg-white overflow-hidden pt-[72px]">
      {/* spotlight glow */}
      <motion.div
        style={{ y: yGlow }}
        className="pointer-events-none absolute -top-40 right-[-10%] w-[70vw] h-[70vw] rounded-full opacity-[0.18]"
      >
        <div className="w-full h-full rounded-full bg-gold blur-[160px]" />
      </motion.div>

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center min-h-[calc(100vh-72px)] py-16">
        {/* Left copy */}
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="w-8 h-px bg-gold" />
            <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-gold">
              Digital Designer · Melbourne
            </span>
          </motion.div>

          <h1 className="font-display font-extrabold text-ink tracking-mega leading-[0.92] text-[13vw] sm:text-7xl lg:text-[5.6rem]">
            <MaskText lines={["Designs that"]} delay={0.35} />
            <span className="block">
              <MaskText
                lines={[
                  <span key="a"><span className="font-editorial italic font-normal text-gold">grow</span>{" "}your</span>,
                  "business.",
                ]}
                delay={0.55}
              />
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9 }}
            className="mt-8 max-w-md text-ink/60 text-base sm:text-lg leading-relaxed"
          >
            Grow your business. Get noticed. MM Digital Agency helps Australian businesses
            build their digital presence through websites, branding, print, social media and
            Google presence. Whether you&apos;re starting from scratch or ready to take your
            business to the next level, we&apos;re here to help your business be seen.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-quote-btn"
              onClick={() => scrollToId("contact")}
              className="group inline-flex items-center gap-2 bg-gold text-ink font-mono text-[12px] uppercase tracking-[0.14em] px-7 py-4 rounded-full hover:bg-cream transition-colors duration-300"
            >
              Get a Quote
              <ArrowUpRight weight="bold" size={16} className="group-hover:rotate-45 transition-transform duration-300" />
            </button>
            <button
              data-testid="hero-work-btn"
              onClick={() => scrollToId("pricing")}
              className="inline-flex items-center gap-2 border border-ink/25 text-ink font-mono text-[12px] uppercase tracking-[0.14em] px-7 py-4 rounded-full hover:border-gold hover:text-gold transition-colors duration-300"
            >
              View Prices
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 1 }}
            className="mt-14 flex items-center gap-8 sm:gap-12"
          >
            {STATS.map((s) => (
              <div key={s.v} data-testid={`hero-stat-${s.k}`}>
                <div className="font-display text-3xl font-bold text-ink">{s.k}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/45 mt-1">{s.v}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right image — clipped frame + parallax */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-[2px] border border-ink/10" style={{ aspectRatio: "4/5" }}>
              <motion.img
                style={{ y: yImg, scale: scaleImg }}
                src="https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Website design workspace"
                className="w-full h-[115%] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white/30 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-cream text-ink px-5 py-4 rounded-[2px] max-w-[200px] shadow-2xl">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-deep">Now booking</div>
              <div className="font-display font-bold text-lg leading-tight mt-1">Projects for 2026</div>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.button
        onClick={() => scrollToId("services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-ink/40 flex flex-col items-center gap-2"
        data-testid="hero-scroll-cue"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  );
}
